import { BaseBlok, BlokToComponentProps, blokToComponent, useTranslation } from '@repo/roche-xx-shared';
import { RocheUIDialog, RocheUIDialogPosition, RocheUIIcon } from '@rocheglobal/roche-ui';
import React, { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { getComponent } from '../../helpers/get-component';
import { preventDialogScroll } from '../../helpers/prevent-dialog-scroll/index';

type RocheModalBlockProps = BlokToComponentProps<{
  slotted: BaseBlok[];
}>;

export interface RocheModalProps extends RocheModalBlockProps {
  dialogLabel: string;
  children?: ReactNode;
}

export const RocheModal: FC<RocheModalProps> = ({ blok, dialogLabel, children }) => {
  const [isMounted, setIsMounted] = useState(false);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const closeButtonLabel = useTranslation('close');

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!isMounted || !dialog) return undefined;

    const handleDialogClose = () => {
      window.dispatchEvent(new Event('rocheModalClose'));
      setIsMounted(false);
    };

    dialog.addEventListener('close', handleDialogClose);
    dialog.showModal();
    preventDialogScroll(dialog);

    return () => {
      dialog.removeEventListener('close', handleDialogClose);
    };
  }, [isMounted]);

  const handleClose = () => {
    dialogRef.current?.close();
  };

  const slottedChildren = blok.slotted?.map((item) => blokToComponent({ getComponent, blok: item }, 'roche-modal'));

  if (!isMounted) {
    return null;
  }

  return createPortal(
    <RocheUIDialog
      ref={dialogRef}
      isOpen={isMounted}
      aria-label={dialogLabel}
      variant={RocheUIDialogPosition.CENTER}
      onCancel={handleClose}
      enableEscapeKey
    >
      <RocheUIDialog.Head>
        <button type='button' onClick={handleClose} aria-label={closeButtonLabel}>
          <RocheUIIcon icon='mobile-close' height='32px' />
        </button>
      </RocheUIDialog.Head>
      <RocheUIDialog.Content>
        {slottedChildren}
        {children}
      </RocheUIDialog.Content>
    </RocheUIDialog>,
    document.body,
  );
};
